import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@apollo/client/react";

import Box from "ds/components/Box";
import Button from "ds/components/Button";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import FlashContext from "components/FlashMessages/FlashContext";
import PageInfoHeader from "ds/components/PageInfoHeader";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageOrganization } from "hooks/useAnalytics/pages/organization";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { AccountContext } from "views/AccountWrapper";

import { UPDATE_SECURITY_EMAIL } from "./gql";

type SecuritySettingsProps = {
  securityEmail: string;
};

type FormFields = {
  securityEmail: string;
};

const SecuritySettings = ({ securityEmail }: SecuritySettingsProps) => {
  const { accountName } = useTypedContext(AccountContext);
  const { onError, reportSuccess } = useTypedContext(FlashContext);

  useTitle(`Organization Settings · Security Settings · ${accountName}`);

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageOrganization.OrganizationSecurityEmail,
  });

  const {
    register,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<FormFields>({
    defaultValues: { securityEmail: securityEmail || "" },
    mode: "onChange",
  });

  useEffect(() => {
    reset({ securityEmail: securityEmail || "" });
  }, [reset, securityEmail]);

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const [updateSecurityEmail, { loading }] = useMutation(UPDATE_SECURITY_EMAIL, {
    refetchQueries: ["GetAccountSettings"],
  });

  const onSubmit = async ({ securityEmail: email }: FormFields) => {
    try {
      trackSegmentAnalyticsEvent("Save");
      await updateSecurityEmail({ variables: { securityEmail: email } });
      reportSuccess({ message: "Security Email Contact Updated" });
    } catch (e) {
      onError(e);
    }
  };

  return (
    <>
      <PageInfoHeader title="Security email" />
      <Box direction="column" padding="x-large" gap="x-large">
        <FormField
          label="Security email"
          tooltipInfo="This is an email we will use to contact you in case of any security incidents."
          tooltipWidthMode="maxWidthSm"
          noMargin
        >
          {({ ariaInputProps }) => (
            <Input
              type="text"
              placeholder="Your account's security email contact"
              {...register("securityEmail")}
              {...ariaInputProps}
            />
          )}
        </FormField>
        <Box justify="end" gap="large">
          {isDirty && (
            <Button variant="secondary" onPress={() => reset()}>
              Cancel
            </Button>
          )}
          <Button
            variant="primary"
            onPress={() => handleSubmit(onSubmit)()}
            disabled={!isDirty || loading}
            loading={loading}
          >
            Save
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default SecuritySettings;
